import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useCity } from "@/context/CityContext";
import { useLive } from "@/context/LiveContext";
import CitySelect from "@/components/CitySelect";
import NotificationBell from "@/components/NotificationBell";
import FlipCounter from "@/components/FlipCounter";
import { openInstall } from "@/components/InstallPrompt";
import { Menu, X, LayoutDashboard, LogOut, User, Smartphone } from "lucide-react";

const LINKS = [["/", "Home", "nav-home"], ["/properties", "Properties", "nav-properties"], ["/highlighted", "Highlighted", "nav-highlighted"], ["/blogs", "Blogs", "nav-blogs"], ["/pricing", "Pricing", "nav-pricing"], ["/contact", "Contact", "nav-contact"]];
const ROTATE = ["venue", "catering", "decor", "photography", "anchor", "band"];

export const dashboardPath = (user) => {
  if (!user) return "/login";
  if (["super_admin", "major_admin", "admin"].includes(user.role)) return "/admin";
  if (user.role === "venue_owner" || user.role === "staff") return "/owner";
  return "/account";
};

export default function Navbar() {
  const { user, logout } = useAuth();
  const { city, setCity } = useCity();
  const { visitors } = useLive();
  const [open, setOpen] = useState(false);
  const [word, setWord] = useState(0);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => { const t = setInterval(() => setWord((w) => (w + 1) % ROTATE.length), 1800); return () => clearInterval(t); }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const pickCity = (c) => { setCity(c); if (c) nav(`/properties?city=${encodeURIComponent(c)}`); };
  const doLogout = async () => { await logout(); setOpen(false); nav("/"); };

  return (
    <header className="glass-nav sticky top-0 z-50" style={{ paddingTop: "env(safe-area-inset-top, 0px)" }} data-testid="site-navbar">
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 h-[68px] flex items-center gap-3">
        <Link to="/" className="flex items-center shrink-0 press" data-testid="brand-link" aria-label="DartMyVenue home">
          <img src="/logo.png" alt="DartMyVenue" className="h-[42px] md:h-[46px] w-auto"/>
        </Link>

        <nav className="hidden xl:flex items-center gap-1 ml-6">
          {LINKS.map(([p, l, t]) => (
            <NavLink key={p} to={p} end={p === "/"} className={({ isActive }) => `relative px-3 py-2 rounded-full text-[15px] transition-colors ${isActive ? "text-[color:var(--maroon)] font-semibold" : "text-[color:var(--ink-2)] hover:text-[color:var(--maroon)] hover:bg-[color:var(--ivory)]"}`} data-testid={t}>
              {({ isActive }) => (<>{l}{isActive && <motion.span layoutId="nav-dot" className="absolute left-1/2 -translate-x-1/2 bottom-0.5 w-1.5 h-1.5 rounded-full bg-[color:var(--maroon)]"/>}</>)}
            </NavLink>
          ))}
        </nav>

        <div className="flex-1"/>

        <div className="hidden md:block w-44"><CitySelect value={city} onChange={pickCity} allowAll testId="navbar-city-dropdown"/></div>

        <div className="hidden lg:flex items-center px-1" data-testid="visitor-counter" title="People who have visited DartMyVenue">
<FlipCounter value={visitors} digits={6} size="xs" label="visitors" labelClassName="text-[11px] text-[color:var(--ink-2)] font-semibold tracking-wide"/>
        </div>

        <NotificationBell/>

        {user ? (
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <Link to={dashboardPath(user)} className="inline-flex items-center gap-2 pl-1 pr-4 py-1 rounded-full border border-[color:var(--line)] bg-white hover:border-[color:var(--maroon)] transition-colors press" data-testid="dashboard-link">
              <span className="w-8 h-8 rounded-full bg-[color:var(--maroon)] text-white flex items-center justify-center"><LayoutDashboard className="w-4 h-4"/></span>
              <span className="text-[15px] font-semibold text-[color:var(--maroon)]">My panel</span>
            </Link>
            <button onClick={doLogout} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-[color:var(--line)] bg-white text-[15px] font-semibold text-[color:var(--ink-2)] hover:text-white hover:bg-rose-700 hover:border-rose-700 transition-colors press" data-testid="logout-btn">
              <LogOut className="w-4 h-4"/> Logout
            </button>
          </div>
        ) : (
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <Link to="/login" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-[color:var(--line)] bg-white text-[15px] font-semibold text-[color:var(--maroon)] hover:border-[color:var(--maroon)] press" data-testid="login-icon"><User className="w-4 h-4"/> Sign in</Link>
            <Link to="/register?role=venue_owner" className="btn-wine !py-2.5 !px-5 whitespace-nowrap" data-testid="register-link">
              <span className="inline-flex items-center gap-[0.35em] leading-none">
                <span>List</span>
                <span className="relative inline-block h-[1.2em] w-[6.6em] overflow-hidden text-left" aria-live="polite">
                  <AnimatePresence initial={false}>
                    <motion.span key={ROTATE[word]} initial={{ y: "110%" }} animate={{ y: "0%" }} exit={{ y: "-110%" }} transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                      className="absolute left-0 top-0 h-[1.2em] leading-[1.2em] text-[color:var(--gold-2)]" data-testid="register-rotating-word">{ROTATE[word]}</motion.span>
                  </AnimatePresence>
                </span>
              </span>
            </Link>
          </div>
        )}

        <button className="xl:hidden w-10 h-10 rounded-full flex items-center justify-center hover:bg-[color:var(--ivory-2)] press" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menu" aria-expanded={open}>
          {open ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="xl:hidden overflow-hidden border-t border-[color:var(--line)] bg-[color:var(--cream)]">
            <div className="px-5 py-5 flex flex-col gap-1 text-[16px]">
              <div className="md:hidden mb-2"><CitySelect value={city} onChange={(c) => { pickCity(c); setOpen(false); }} allowAll testId="mobile-city-dropdown"/></div>
              {LINKS.map(([p, l]) => (
                <NavLink key={p} to={p} end={p === "/"} className={({ isActive }) => `py-2.5 px-3 rounded-xl ${isActive ? "bg-[color:var(--ivory)] text-[color:var(--maroon)] font-semibold" : "text-[color:var(--ink)]"}`}>{l}</NavLink>
              ))}
              <div className="flex items-center justify-between mt-3 px-3 py-3 rounded-xl bg-white border border-[color:var(--line)]">
                <span className="text-sm text-[color:var(--ink-2)] font-semibold">Visitors so far</span>
                <FlipCounter value={visitors} digits={6} size="xs"/>
              </div>
              <button onClick={() => { openInstall(); setOpen(false); }} className="mt-2 py-2.5 px-3 rounded-xl text-left inline-flex items-center gap-2 text-[color:var(--maroon)] font-semibold"><Smartphone className="w-4 h-4"/> Add DartMyVenue to your home screen</button>
              {user ? (
                <div className="flex gap-2 pt-3">
                  <Link to={dashboardPath(user)} className="btn-ghost flex-1"><LayoutDashboard className="w-4 h-4"/> My panel</Link>
                  <button onClick={doLogout} className="btn-wine flex-1" data-testid="mobile-logout-btn"><LogOut className="w-4 h-4"/> Logout</button>
                </div>
              ) : (
                <div className="flex gap-2 pt-3">
                  <Link to="/login" className="btn-ghost flex-1">Sign in</Link>
                  <Link to="/register?role=venue_owner" className="btn-wine flex-1">List your business</Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
